import classnames from 'classnames/bind';
import PropTypes from 'prop-types';
import EvStationPropType from '../../prop-types/evStation';
import styles from './EvStationListItem.module.css';

const cx = classnames.bind(styles);

/**
 * Get status display info
 * Only shows status for non-operational stations (not "Available")
 */
function getStatusInfo(statusCode) {
  switch (statusCode) {
    case 'E':
      // Don't show "Available" status - only show when there's an issue
      return null;
    case 'P':
      return { label: 'Planned', className: 'statusPlanned' };
    case 'T':
      return { label: 'Temporarily Unavailable', className: 'statusUnavailable' };
    default:
      return null;
  }
}

/**
 * Get connector types to display
 */
function getConnectorList(station) {
  const connectors = [];

  if (station.evJ1772ConnectorCount > 0) {
    connectors.push({ code: 'J1772', count: station.evJ1772ConnectorCount });
  }
  if (station.evCcsConnectorCount > 0) {
    connectors.push({ code: 'CCS', count: station.evCcsConnectorCount });
  }
  if (station.evChademoConnectorCount > 0) {
    connectors.push({ code: 'CHAdeMO', count: station.evChademoConnectorCount });
  }
  if (station.evJ3400ConnectorCount > 0) {
    connectors.push({ code: 'Tesla/NACS', count: station.evJ3400ConnectorCount });
  }

  return connectors;
}

/**
 * Get the color class based on station's highest charging level
 */
function getMarkerColorClass(station) {
  if (station.evDcFastNum > 0) {
    return 'markerDcFast';
  }
  if (station.evLevel2EvseNum > 0) {
    return 'markerLevel2';
  }
  return 'markerLevel1';
}

export default function EvStationListItem({
  station,
  isSelected,
  isHighlighted,
  onClick,
  onMouseEnter,
  onMouseLeave,
}) {
  const statusInfo = getStatusInfo(station.statusCode);
  const connectors = getConnectorList(station);
  const markerColorClass = getMarkerColorClass(station);

  const handleClick = () => {
    if (onClick) {
      onClick(station);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleClick();
    }
  };

  // Format distance
  const distanceDisplay = station.distanceMiles != null
    ? `${station.distanceMiles.toFixed(1)} mi`
    : null;

  // Format address
  const addressParts = [
    station.streetAddress,
    station.city,
    station.state,
  ].filter(Boolean);
  const addressDisplay = addressParts.join(', ');

  return (
    <div
      className={cx('stationListItem', {
        selected: isSelected,
        highlighted: isHighlighted,
      })}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      role="button"
      tabIndex={0}
    >
      <div className={cx('header')}>
        {/* Icon Column - Station icon with distance below */}
        <div className={cx('iconColumn')}>
          <div className={cx('stationIcon', markerColorClass, {
            underRepair: station.statusCode === 'T',
          })}
          />
          {distanceDisplay && (
            <span className={cx('distanceBelow')}>{distanceDisplay}</span>
          )}
        </div>

        <div className={cx('stationInfo')}>
          <h3 className={cx('stationName')}>{station.stationName}</h3>
          <p className={cx('address')}>{addressDisplay}</p>
          {station.evNetwork && (
            <span className={cx('networkBadge')}>{station.evNetwork}</span>
          )}
        </div>

        {/* Charger counts - positioned to the right of station info */}
        <div className={cx('chargerColumn')}>
          {station.evDcFastNum > 0 && (
            <div className={cx('chargerType', 'dcFast')}>
              <span className={cx('chargerCount')}>{station.evDcFastNum}</span>
              <span className={cx('chargerLabel')}>DC Fast</span>
            </div>
          )}
          {station.evLevel2EvseNum > 0 && (
            <div className={cx('chargerType', 'level2')}>
              <span className={cx('chargerCount')}>{station.evLevel2EvseNum}</span>
              <span className={cx('chargerLabel')}>Level 2</span>
            </div>
          )}
          {station.evLevel1EvseNum > 0 && (
            <div className={cx('chargerType', 'level1')}>
              <span className={cx('chargerCount')}>{station.evLevel1EvseNum}</span>
              <span className={cx('chargerLabel')}>Level 1</span>
            </div>
          )}
        </div>
      </div>

      {/* Status badge - only shown for non-available stations */}
      {statusInfo && (
        <div className={cx('statusRow')}>
          <span className={cx('statusBadge', statusInfo.className)}>
            {statusInfo.label}
          </span>
        </div>
      )}

      {connectors.length > 0 && (
        <div className={cx('connectors')}>
          {connectors.map(({ code, count }) => (
            <span key={code} className={cx('connector')}>
              {code} ({count})
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

EvStationListItem.propTypes = {
  station: EvStationPropType.isRequired,
  isSelected: PropTypes.bool,
  isHighlighted: PropTypes.bool,
  onClick: PropTypes.func,
  onMouseEnter: PropTypes.func,
  onMouseLeave: PropTypes.func,
};

EvStationListItem.defaultProps = {
  isSelected: false,
  isHighlighted: false,
  onClick: undefined,
  onMouseEnter: undefined,
  onMouseLeave: undefined,
};
